import React, { useState, useEffect } from 'react';
import { Save, Building2, FileText, DollarSign, Mail, Phone, MapPin, Hash, Percent } from 'lucide-react';

export const Preferences = () => {
  const [preferences, setPreferences] = useState({
    // Company Information
    companyName: 'JFT Contracting Inc',
    companyAddress: '123 Main Street',
    companyCity: 'Thunder Bay',
    companyProvince: 'ON',
    companyPostalCode: 'P7A 1A1',
    companyPhone: '(807) 555-1234',
    companyEmail: 'info@jftcontracting.com',
    companyWebsite: 'www.jftcontracting.com',

    // Tax & Business Numbers
    hstNumber: '123456789 RT0001',
    businessNumber: '',

    // Invoice Settings
    invoicePrefix: 'INV-',
    invoiceTerms: 'Net 30',
    defaultTaxRate: 13,
    taxLabel: 'HST',

    // Estimate Settings
    estimatePrefix: 'EST-',
    estimateValidityDays: 30,

    // Banking Information
    bankName: '',
    transitNumber: '',
    institutionNumber: '',
    accountNumber: '',

    // Display Settings
    currencySymbol: '$',
    dateFormat: 'en-CA',
    logoUrl: '/jft-logo.png',

    // Email Settings
    emailSignature: 'Best regards,\nJFT Contracting Team',

    // Payment Instructions
    paymentInstructions: 'Please make checks payable to JFT Contracting Inc.\nE-transfer accepted at the email above.',
  });

  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    // Load preferences from localStorage
    const savedPreferences = localStorage.getItem('appPreferences');
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem('appPreferences', JSON.stringify(preferences));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const tabs = [
    { id: 'company', label: 'Company Info', icon: Building2 },
    { id: 'invoice', label: 'Invoice Settings', icon: FileText },
    { id: 'banking', label: 'Banking', icon: DollarSign },
    { id: 'display', label: 'Display', icon: Hash },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Preferences</h1>
              <p className="text-gray-600 mt-1">Manage your company information and application settings</p>
            </div>
            <button
              onClick={handleSave}
              className={`px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                saved
                  ? 'bg-green-600 text-white'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
            >
              <Save size={20} />
              <span>{saved ? 'Saved!' : 'Save Changes'}</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 text-sm font-medium border-b-2 flex items-center space-x-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Company Info Tab */}
            {activeTab === 'company' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={preferences.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <Phone size={16} />
                      <span>Phone</span>
                    </label>
                    <input
                      type="text"
                      value={preferences.companyPhone}
                      onChange={(e) => handleChange('companyPhone', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <Mail size={16} />
                      <span>Email</span>
                    </label>
                    <input
                      type="email"
                      value={preferences.companyEmail}
                      onChange={(e) => handleChange('companyEmail', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="text"
                    value={preferences.companyWebsite}
                    onChange={(e) => handleChange('companyWebsite', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                    <MapPin size={16} />
                    <span>Street Address</span>
                  </label>
                  <input
                    type="text"
                    value={preferences.companyAddress}
                    onChange={(e) => handleChange('companyAddress', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={preferences.companyCity}
                      onChange={(e) => handleChange('companyCity', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Province
                    </label>
                    <select
                      value={preferences.companyProvince}
                      onChange={(e) => handleChange('companyProvince', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="AB">Alberta</option>
                      <option value="BC">British Columbia</option>
                      <option value="MB">Manitoba</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="ON">Ontario</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="QC">Quebec</option>
                      <option value="SK">Saskatchewan</option>
                      <option value="NT">Northwest Territories</option>
                      <option value="NU">Nunavut</option>
                      <option value="YT">Yukon</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={preferences.companyPostalCode}
                      onChange={(e) => handleChange('companyPostalCode', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HST Number
                    </label>
                    <input
                      type="text"
                      value={preferences.hstNumber}
                      onChange={(e) => handleChange('hstNumber', e.target.value)}
                      placeholder="123456789 RT0001"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={preferences.businessNumber}
                      onChange={(e) => handleChange('businessNumber', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Invoice Settings Tab */}
            {activeTab === 'invoice' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Number Prefix
                    </label>
                    <input
                      type="text"
                      value={preferences.invoicePrefix}
                      onChange={(e) => handleChange('invoicePrefix', e.target.value)}
                      placeholder="INV-"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="text-sm text-gray-500 mt-1">Example: INV-2024-001</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimate Number Prefix
                    </label>
                    <input
                      type="text"
                      value={preferences.estimatePrefix}
                      onChange={(e) => handleChange('estimatePrefix', e.target.value)}
                      placeholder="EST-"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="text-sm text-gray-500 mt-1">Example: EST-2024-001</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Payment Terms
                    </label>
                    <select
                      value={preferences.invoiceTerms}
                      onChange={(e) => handleChange('invoiceTerms', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="Due on Receipt">Due on Receipt</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimate Validity (Days)
                    </label>
                    <input
                      type="number"
                      value={preferences.estimateValidityDays}
                      onChange={(e) => handleChange('estimateValidityDays', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <Percent size={16} />
                      <span>Default Tax Rate (%)</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={preferences.defaultTaxRate}
                      onChange={(e) => handleChange('defaultTaxRate', parseFloat(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax Label
                    </label>
                    <input
                      type="text"
                      value={preferences.taxLabel}
                      onChange={(e) => handleChange('taxLabel', e.target.value)}
                      placeholder="HST, GST, PST, etc."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Instructions
                  </label>
                  <textarea
                    value={preferences.paymentInstructions}
                    onChange={(e) => handleChange('paymentInstructions', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Instructions for customers on how to pay invoices..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Signature
                  </label>
                  <textarea
                    value={preferences.emailSignature}
                    onChange={(e) => handleChange('emailSignature', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Default signature for emails..."
                  />
                </div>
              </div>
            )}

            {/* Banking Tab */}
            {activeTab === 'banking' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This banking information is optional and will be used for payment processing and direct deposit features.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={preferences.bankName}
                    onChange={(e) => handleChange('bankName', e.target.value)}
                    placeholder="e.g., Royal Bank of Canada"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transit Number
                    </label>
                    <input
                      type="text"
                      value={preferences.transitNumber}
                      onChange={(e) => handleChange('transitNumber', e.target.value)}
                      placeholder="12345"
                      maxLength="5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Institution Number
                    </label>
                    <input
                      type="text"
                      value={preferences.institutionNumber}
                      onChange={(e) => handleChange('institutionNumber', e.target.value)}
                      placeholder="001"
                      maxLength="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={preferences.accountNumber}
                      onChange={(e) => handleChange('accountNumber', e.target.value)}
                      placeholder="1234567"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Display Tab */}
            {activeTab === 'display' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo URL
                  </label>
                  <input
                    type="text"
                    value={preferences.logoUrl}
                    onChange={(e) => handleChange('logoUrl', e.target.value)}
                    placeholder="/jft-logo.png"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">Path to your company logo in the public folder</p>
                  {preferences.logoUrl && (
                    <div className="mt-3">
                      <img
                        src={preferences.logoUrl}
                        alt="Company Logo Preview"
                        className="h-16 border border-gray-200 rounded p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency Symbol
                    </label>
                    <input
                      type="text"
                      value={preferences.currencySymbol}
                      onChange={(e) => handleChange('currencySymbol', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Format Locale
                    </label>
                    <select
                      value={preferences.dateFormat}
                      onChange={(e) => handleChange('dateFormat', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="en-CA">Canadian English (YYYY-MM-DD)</option>
                      <option value="en-US">US English (MM/DD/YYYY)</option>
                      <option value="en-GB">UK English (DD/MM/YYYY)</option>
                      <option value="fr-CA">Canadian French</option>
                    </select>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Preview</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Currency:</strong> {preferences.currencySymbol}1,234.56</p>
                    <p><strong>Date:</strong> {new Date().toLocaleDateString(preferences.dateFormat)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-sm text-orange-800">
            <strong>Tip:</strong> Changes will be saved locally and applied to all invoices, estimates, and reports. Make sure to click "Save Changes" after making modifications.
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function to get preferences (can be imported in other components)
export const getPreferences = () => {
  const savedPreferences = localStorage.getItem('appPreferences');
  if (savedPreferences) {
    return JSON.parse(savedPreferences);
  }

  // Return defaults if none saved
  return {
    companyName: 'JFT Contracting Inc',
    companyAddress: '123 Main Street',
    companyCity: 'Thunder Bay',
    companyProvince: 'ON',
    companyPostalCode: 'P7A 1A1',
    companyPhone: '(807) 555-1234',
    companyEmail: 'info@jftcontracting.com',
    companyWebsite: 'www.jftcontracting.com',
    hstNumber: '123456789 RT0001',
    businessNumber: '',
    invoicePrefix: 'INV-',
    invoiceTerms: 'Net 30',
    defaultTaxRate: 13,
    taxLabel: 'HST',
    estimatePrefix: 'EST-',
    estimateValidityDays: 30,
    bankName: '',
    transitNumber: '',
    institutionNumber: '',
    accountNumber: '',
    currencySymbol: '$',
    dateFormat: 'en-CA',
    logoUrl: '/jft-logo.png',
    emailSignature: 'Best regards,\nJFT Contracting Team',
    paymentInstructions: 'Please make checks payable to JFT Contracting Inc.\nE-transfer accepted at the email above.',
  };
};
